// Fchier du  dashboard du service client (A mettre à jour en ajouter  uniquement , ce qui est nouvellement ajouté dans la version recente du dashboard principal )

import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { DollarSign, Plus, Edit, Trash2, RefreshCw, TrendingUp, Loader2, Inbox, Save } from "lucide-react";
import useExchangeRates from "../../../../hooks/administration/settings/useExchangeRates";
import useSystemSettings from "../../../../hooks/administration/settings/useSystemSettings";
import { ExchangeRate } from "../../../../types/features/administration/Setting";

type SettingsTab = "exchange" | "withdrawal-email";

const VALID_TABS: SettingsTab[] = ["exchange", "withdrawal-email"];

const SettingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") as SettingsTab | null;
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : "exchange"
  );

  const selectTab = (tab: SettingsTab) => {
    setActiveTab(tab);
    setSearchParams(tab === "exchange" ? {} : { tab }, { replace: true });
  };

  useEffect(() => {
    const tab = searchParams.get("tab") as SettingsTab | null;
    if (tab && VALID_TABS.includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const [actionLoading, setActionLoading] = useState(false);

  // Hooks
  const { rates: exchangeRates, loading: ratesLoading, createRate, updateRate, deleteRate } = useExchangeRates();

  const { settings: systemSettings, loading: settingsLoading, updateWithdrawalNotificationEmail } = useSystemSettings();

  // Exchange Rates State
  const [showAddRateModal, setShowAddRateModal] = useState(false);
  const [showEditRateModal, setShowEditRateModal] = useState(false);
  const [newRate, setNewRate] = useState<Partial<ExchangeRate>>({
    from_currency: "",
    to_currency: "",
    rate: 0,
    status: "ACTIVE",
  });
  const [editingRate, setEditingRate] = useState<ExchangeRate | null>(null);

  // Withdrawal Email State
  const [localWithdrawalEmail, setLocalWithdrawalEmail] = useState("");

  // Sync system settings when loaded
  useEffect(() => {
    if (systemSettings) {
      setLocalWithdrawalEmail(systemSettings.withdrawal_notification_email || "");
    }
  }, [systemSettings]);

  // Exchange Rate Handlers
  const handleAddRate = async () => {
    if (newRate.from_currency && newRate.to_currency && newRate.rate) {
      setActionLoading(true);
      try {
        await createRate(newRate as Omit<ExchangeRate, "id" | "created_at" | "updated_at">);
        setNewRate({ from_currency: "", to_currency: "", rate: 0, status: "ACTIVE" });
        setShowAddRateModal(false);
        alert("Taux créé avec succès !");
      } catch (error) {
        alert("Erreur lors de la création du taux");
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleDeleteRate = async (fromCurrency: string, toCurrency: string) => {
    if (confirm(`Êtes-vous sûr de vouloir supprimer le taux ${fromCurrency}/${toCurrency} ?`)) {
      setActionLoading(true);
      try {
        await deleteRate(fromCurrency, toCurrency);
        alert("Taux supprimé avec succès !");
      } catch (error) {
        alert("Erreur lors de la suppression du taux");
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleToggleRateStatus = async (
    fromCurrency: string,
    toCurrency: string,
    currentStatus: "ACTIVE" | "INACTIVE"
  ) => {
    setActionLoading(true);
    try {
      const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      await updateRate(fromCurrency, toCurrency, { status: newStatus });
      alert(`Statut mis à jour avec succès !`);
    } catch (error) {
      alert("Erreur lors de la mise à jour du statut");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditRate = (rate: ExchangeRate) => {
    setEditingRate(rate);
    setShowEditRateModal(true);
  };

  const handleUpdateRate = async () => {
    if (!editingRate) return;

    setActionLoading(true);
    try {
      await updateRate(editingRate.from_currency, editingRate.to_currency, {
        rate: editingRate.rate,
        status: editingRate.status,
      });
      setShowEditRateModal(false);
      setEditingRate(null);
      alert("Taux modifié avec succès !");
    } catch (error) {
      alert("Erreur lors de la modification du taux");
    } finally {
      setActionLoading(false);
    }
  };

  // Withdrawal Email Handler
  const handleSaveWithdrawalEmail = async () => {
    setActionLoading(true);
    try {
      await updateWithdrawalNotificationEmail(localWithdrawalEmail.trim() || null);
      alert("Email de notification de retrait sauvegardé avec succès !");
    } catch (error) {
      alert("Erreur lors de la sauvegarde de l'email");
    } finally {
      setActionLoading(false);
    }
  };

  // Show loading state
  if (ratesLoading || settingsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 w-12 h-12 text-green-600 animate-spin" />
          <p className="text-gray-600">Chargement des paramètres...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-bold text-gray-800">Paramètres</h1>
        <p className="text-gray-600">Configuration des taux de change</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 bg-white rounded-lg border border-gray-200">
        <div className="flex overflow-x-auto border-b border-gray-200">
          <button
            onClick={() => selectTab("exchange")}
            className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
              activeTab === "exchange"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <DollarSign className="w-5 h-5" />
            Taux de Change
          </button>
          <button
            onClick={() => selectTab("withdrawal-email")}
            className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
              activeTab === "withdrawal-email"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <Inbox className="w-5 h-5" />
            Retraits
          </button>
        </div>
      </div>

      {/* Withdrawal Email Tab */}
      {activeTab === "withdrawal-email" && (
        <div className="p-6 space-y-6 bg-white rounded-lg border border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Email de notification des retraits</h2>
            <p className="text-sm text-gray-600">
              Configurez l'adresse email qui recevra les notifications des demandes de retrait des utilisateurs.
            </p>
          </div>

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">Adresse email</label>
            <input
              type="email"
              value={localWithdrawalEmail}
              onChange={(e) => setLocalWithdrawalEmail(e.target.value)}
              placeholder="admin@izilifepay.com"
              className="px-4 py-2 w-full max-w-md rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <button
              onClick={handleSaveWithdrawalEmail}
              disabled={actionLoading}
              className="flex gap-2 items-center px-6 py-2 text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:opacity-50"
            >
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              <Save className="w-4 h-4" />
              Sauvegarder
            </button>
          </div>
        </div>
      )}

      {/* Exchange Rates Tab */}
      {activeTab === "exchange" && (
        <div className="space-y-6">
          {/* Header */}
          <div className="p-6 bg-white rounded-lg border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Taux de Change</h2>
                <p className="text-sm text-gray-600">Gérer les taux de conversion entre devises</p>
              </div>
              <button
                onClick={() => setShowAddRateModal(true)}
                className="flex gap-2 items-center px-4 py-2 text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700"
              >
                <Plus className="w-4 h-4" />
                Ajouter un Taux
              </button>
            </div>

            {/* Rates List */}
            <div className="space-y-3">
              {exchangeRates.length === 0 ? (
                <div className="py-8 text-center text-gray-500">Aucun taux de change configuré</div>
              ) : (
                exchangeRates.map((rate, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center p-4 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100"
                  >
                    <div className="flex gap-4 items-center">
                      <div className="flex justify-center items-center w-12 h-12 bg-green-100 rounded-full">
                        <TrendingUp className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">
                          {rate.from_currency} → {rate.to_currency}
                        </p>
                        <p className="text-2xl font-bold text-green-600">{rate.rate.toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="flex gap-3 items-center">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          rate.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {rate.status}
                      </span>
                      <button
                        onClick={() => handleEditRate(rate)}
                        disabled={actionLoading}
                        className="p-2 text-gray-400 transition-colors hover:text-blue-600 disabled:opacity-50"
                        title="Modifier le taux"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleRateStatus(rate.from_currency, rate.to_currency, rate.status)}
                        disabled={actionLoading}
                        className="p-2 text-gray-400 transition-colors hover:text-orange-600 disabled:opacity-50"
                        title="Changer le statut"
                      >
                        <RefreshCw className={`w-4 h-4 ${actionLoading ? "animate-spin" : ""}`} />
                      </button>
                      <button
                        onClick={() => handleDeleteRate(rate.from_currency, rate.to_currency)}
                        disabled={actionLoading}
                        className="p-2 text-gray-400 transition-colors hover:text-red-600 disabled:opacity-50"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Add Rate Modal */}
          {showAddRateModal && (
            <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
              <div className="p-6 w-full max-w-md bg-white rounded-lg">
                <h3 className="mb-4 text-xl font-bold text-gray-800">Ajouter un Taux de Change</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">Devise Source</label>
                    <input
                      type="text"
                      placeholder="CAD"
                      value={newRate.from_currency}
                      onChange={(e) => setNewRate({ ...newRate, from_currency: e.target.value.toUpperCase() })}
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                      maxLength={3}
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">Devise Cible</label>
                    <input
                      type="text"
                      placeholder="XAF"
                      value={newRate.to_currency}
                      onChange={(e) => setNewRate({ ...newRate, to_currency: e.target.value.toUpperCase() })}
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                      maxLength={3}
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">Taux</label>
                    <input
                      type="number"
                      placeholder="440"
                      value={newRate.rate || ""}
                      onChange={(e) => setNewRate({ ...newRate, rate: parseFloat(e.target.value) })}
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">Statut</label>
                    <select
                      value={newRate.status}
                      onChange={(e) => setNewRate({ ...newRate, status: e.target.value as "ACTIVE" | "INACTIVE" })}
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="INACTIVE">INACTIVE</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleAddRate}
                    disabled={actionLoading}
                    className="flex flex-1 gap-2 justify-center items-center px-4 py-2 text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:opacity-50"
                  >
                    {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Ajouter
                  </button>
                  <button
                    onClick={() => setShowAddRateModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-lg transition-colors hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Rate Modal */}
          {showEditRateModal && editingRate && (
            <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
              <div className="p-6 w-full max-w-md bg-white rounded-lg">
                <h3 className="mb-4 text-xl font-bold text-gray-800">
                  Modifier le Taux {editingRate.from_currency}/{editingRate.to_currency}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">Devise Source</label>
                    <input
                      type="text"
                      value={editingRate.from_currency}
                      disabled
                      className="px-4 py-2 w-full bg-gray-100 rounded-lg border border-gray-300 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">Devise Cible</label>
                    <input
                      type="text"
                      value={editingRate.to_currency}
                      disabled
                      className="px-4 py-2 w-full bg-gray-100 rounded-lg border border-gray-300 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">Nouveau Taux</label>
                    <input
                      type="number"
                      value={editingRate.rate}
                      onChange={(e) => setEditingRate({ ...editingRate, rate: parseFloat(e.target.value) })}
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="423"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">Statut</label>
                    <select
                      value={editingRate.status}
                      onChange={(e) =>
                        setEditingRate({ ...editingRate, status: e.target.value as "ACTIVE" | "INACTIVE" })
                      }
                      className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="INACTIVE">INACTIVE</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleUpdateRate}
                    disabled={actionLoading}
                    className="flex flex-1 gap-2 justify-center items-center px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50"
                  >
                    {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Modifier
                  </button>
                  <button
                    onClick={() => {
                      setShowEditRateModal(false);
                      setEditingRate(null);
                    }}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-lg transition-colors hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SettingsPage;
